/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useRef, useState} from 'react';

import InformativeText from '../../constants/InformativeText';
import {SampleExcel} from '../../constants/SampleExcel';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import downstreamLeasedAPI from '../../services/modules/AM/downAssestManagementAPI';
import listingDownstreamLeasedAPI from '../../services/modules/AM/listingDownstreamLeasedAsset';
import propertyTypeJSON from '../../services/modules/JSON/propertyType';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {
  downPropertyKey,
  downPropertyLabel
} from '../Forms/Constants/IP/dSPropertyKeyLabel';
import DownPropertyForm from '../Forms/DownPropertyForm';
import {emissionUnits} from '../Forms/interface/unitChange';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import TableTitleHeader from './Utils/TableTitleHeader';

const DownstreamPropertyTable: React.FC<{
  selectedYear: string;
  isConfigurator?: boolean;
  isAssetManager?: boolean;
}> = ({selectedYear, isConfigurator, isAssetManager = false}) => {
  interface DataType {
    _id: string;
    name: string;
    mobile: string;
    email: string;
    propertyType: number;
    address: string;
    country: number;
    state: number;
    city: number;
    pincode: number;
    floorSpace: number;
    rentedFrom: string;
    rentedTo: string;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isEdit, setIsEdit] = useState(false);

  const [showJobs, setShowJobs] = useState(false);
  const companyId = useCompanyStore.getState().companyId;
  const propertyType = useJSONStore.getState().propertyType;
  const [showModelInfo, setShowModelInfo] = useState(false);
  const {area} = useCompanyStore.getState().preUnits;

  const type = 'Property';

  useEffect(() => {
    if (selectedYear) {
      setIsRefresh(true);
      listingDownstreamLeasedAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${type}`,
        isAssetManager ? 'dataCollection' : 'valueChain'
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [selectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      listingDownstreamLeasedAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${type}`,
        isAssetManager ? 'dataCollection' : 'valueChain'
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([propertyTypeJSON()])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    downstreamLeasedAPI(APITypeEnum.DELETE_ALL, {
      downstreamleasedasset: _id,
      year: selectedYear
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const downPropertyKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: downPropertyKey.ASSET_NAME,
      label: downPropertyLabel.ASSET_NAME,
      type: objTypes.string
    },
    {
      key: downPropertyKey.FIRST_NAME,
      label: downPropertyLabel.FIRST_NAME,
      type: objTypes.string
    },
    {
      key: downPropertyKey.LAST_NAME,
      label: downPropertyLabel.LAST_NAME,
      type: objTypes.string
    },
    ...(!isAssetManager
      ? [
          {
            key: downPropertyKey.ASSET_MOBILE,
            label: downPropertyLabel.ASSET_MOBILE,
            type: objTypes.string
          },
          {
            key: downPropertyKey.ASSET_EMAIL,
            label: downPropertyLabel.ASSET_EMAIL,
            type: objTypes.string
          }
        ]
      : []),

    {
      key: downPropertyKey.PROPERTY_TYPE,
      label: downPropertyLabel.PROPERTY_TYPE,
      type: objTypes.json,
      format: propertyType
    },
    {
      key: downPropertyKey.FLOOR_SPACE,
      label: downPropertyLabel.FLOOR_SPACE,
      type: objTypes.number,
      unit: area
    },
    {
      key: downPropertyKey.RENTED_FROM,
      label: downPropertyLabel.RENTED_FROM,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },
    {
      key: downPropertyKey.RENTED_TO,
      label: downPropertyLabel.RENTED_TO,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },
    ...(isAssetManager
      ? [
          {
            key: downPropertyKey.TOTAL_EMISSION,
            label: downPropertyLabel.TOTAL_EMISSION,
            type: objTypes.number
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = [
    {
      title: downPropertyLabel.ASSET_NAME,
      dataIndex: downPropertyKey.ASSET_NAME,
      ...SearchFilter(
        downPropertyLabel.ASSET_NAME,
        downPropertyKey.ASSET_NAME as keyof DataType
      )
    },
    ...(!isAssetManager
      ? [
          {
            title: downPropertyLabel.ASSET_MOBILE,
            dataIndex: downPropertyKey.ASSET_MOBILE,
            editable: true,
            // width: '200px',
            type: 'number'
          },
          {
            title: downPropertyLabel.ASSET_EMAIL,
            dataIndex: downPropertyKey.ASSET_EMAIL,
            editable: true,
            // ...sorter(downPropertyKey.ASSET_EMAIL as keyof DataType),
            // width: '200px',
            ...SearchFilter(
              downPropertyLabel.ASSET_EMAIL,
              downPropertyKey.ASSET_EMAIL as keyof DataType
            )
          }
        ]
      : []),
    {
      title: downPropertyLabel.PROPERTY_TYPE,
      dataIndex: downPropertyKey.PROPERTY_TYPE,
      editable: true,
      render: (value) => {
        return (
          <>
            {
              propertyType.find((item) => {
                return item.value === value;
              })?.label
            }
          </>
        );
      }
      // width: '200px',
    },
    {
      title: downPropertyLabel.FLOOR_SPACE,
      type: 'number',
      dataIndex: downPropertyKey.FLOOR_SPACE,
      editable: true,
      // ...sorter(downPropertyKey.FLOOR_SPACE as keyof DataType),
      render: (value) => {
        return `${formatIndian(value)} ${value ? area : ''}`;
      }
    },
    {
      title: downPropertyLabel.RENTED_FROM,
      dataIndex: downPropertyKey.RENTED_FROM,
      editable: true,
      render: (value) => moment(value).format('DD-MM-YYYY')
    },
    {
      title: downPropertyLabel.RENTED_TO,
      dataIndex: downPropertyKey.RENTED_TO,
      editable: true,
      render: (value) => moment(value).format('DD-MM-YYYY')
    },
    ...((hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)) &&
    !isConfigurator &&
    !isAssetManager
      ? [
          {
            title: makeCheckKeyLabel.STATUS_COMMENTS.label,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <div
                  style={{
                    fontWeight: 700,
                    color: colorText.color,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}{' '}
                  {value === 'rejected' && record?.comment && (
                    <span className="customComment"> - {record.comment}</span>
                  )}
                </div>
              );
            }
          }
        ]
      : []),
    ...(isAssetManager
      ? [
          {
            title: downPropertyLabel.TOTAL_EMISSION,
            dataIndex: downPropertyKey.TOTAL_EMISSION,
            editable: true,
            // ...sorter(upstreamKey.TOTAL_FUEL_EMISSIONS as keyof DataType),
            //   width: '100px',
            type: 'select',
            // filterer: monthlyYearly,            //   return {text: item.label, value: item.value};
            // }),
            // onFilter: (value, record) => {
            //   return record.totalEmission === value;
            // },
            render: (value: any) => {
              return `${formatIndian(value)} ${emissionUnits.T_CO2e}`;
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            title: makeCheckKeyLabel.STATUS.label,
            // dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(statusEm.ACCEPTED);

              return (
                <Tag
                  onClick={() => {
                    // setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  color={colorText.color}
                  style={{
                    fontWeight: 700
                  }}
                >
                  {colorText.text}
                </Tag>
              );
            }
          }
        ]
      : []),
    // ...(hasUserRole(UserRole.Checker_Role) ||
    // (hasUserRole(UserRole.Both_role) && !isConfigurator)
    //   ? [
    //       {
    //         title: FullInfo.title,
    //         dataIndex: 'FullInfo',
    //         editable: true,
    //         type: 'select',
    //         render: (_: any, record: any) => {
    //           return (
    //             <Button
    //               onClick={() => {
    //                 setShowModelInfo(true);
    //                 setEditData(record);
    //                 setIsEdit(true);
    //               }}
    //               shape="circle"
    //               style={{
    //                 marginRight: 16,
    //                 cursor: 'pointer',
    //                 color: '#435B37',
    //                 background: '#F5FBF3',
    //                 border: 0
    //               }}
    //             >
    //               <ExclamationOutlined />
    //             </Button>
    //           );
    //         }
    //       }
    //     ]
    //   : []),
    ...((isConfigurator ? !hasUserRole(UserRole.View_Only) : false)
      ? [
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '' as const,
            fixed: 'right' as const,
            width: '200px',
            render: (value: any, record: any) => {
              return dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  isConfigurator={isConfigurator}
                />
              ) : null;
            }
          }
        ]
      : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Downstream Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Property'
        }}
        sampleDownload={{href: SampleExcel.AssetDownProperty}}
        jobs={{
          setShowJobs,
          uploadType: uploadTypes.AssetDownProperty
        }}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        onlyYear={true}
        tooltip={InformativeText.AM.downLeased}
        showAddOnly={!isConfigurator}
        onlyUpload={!isConfigurator}
        shouldEdit={
          isConfigurator
            ? !hasUserRole(UserRole.View_Only)
            : hasUserRole(UserRole.Maker_Role) ||
              hasUserRole(UserRole.Both_role)
        }
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={
          isConfigurator &&
          (hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role))
            ? {
                type: 'checkbox',
                selectedRowKeys,
                onChange: (
                  selectedKey: React.Key[],
                  selectedRow: DataType[]
                ) => {
                  setSelectedRowKeys(
                    selectedRow.map((item: any) => {
                      return item._id;
                    })
                  );
                }
              }
            : undefined
        }
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.AssetDownProperty}
        details={`${null}/${selectedYear}`}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={<DownPropertyForm />}
        API={downstreamLeasedAPI}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        type={{key: 'type', value: type}}
        onlyYear={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        stepsTitle={['Property Details']}
        formPreview={downPropertyKeyLabel}
        isConfigurator={isConfigurator}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={downPropertyKeyLabel} />
        }
        API={downstreamLeasedAPI}
        isChecker={true}
        stepsTitle={['Status & Comments']}
        isAction={false}
        // formPreview={downPropertyKeyLabel}
      />
    </div>
  );
};

export default DownstreamPropertyTable;
